<template>
  <div class="split-wrapper mt-4">
    <div class="px-4">
      <v-expansion-panels flat focusable class="mb-4">
        <v-expansion-panel>
            <v-expansion-panel-header color="base">
              <v-icon round>mdi-information-variant</v-icon>
              <h4>{{ $t('splitEventInfo.helpTitle') }}</h4>
            </v-expansion-panel-header>

            <v-expansion-panel-content elevation="6" color="base" class="panel-content py-1">
              {{ $t('splitEventInfo.help') }}
            </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div v-for="(range, index) in dateRanges" :key="'range-'+index">

        <div class="d-flex justify-space-between align-center pb-2">
          <div v-if="range.values.interventionId > 0" class="subtitle-2">
            {{ $t('splitEventInfo.titlePrefix') }}{{ range.values.interventionId }}
          </div>
          <div v-else class="subtitle-2">
            {{ $t('splitEventInfo.newSplit') }}
          </div>

          <div v-if="index > 0" align=end>
            <v-btn icon elevation="0" @click="dateRanges.splice(index, 1)"><v-icon>mdi-close</v-icon></v-btn>
          </div>
        </div>

        <!-- DatePickers -->
        <div class="d-flex justify-space-between align-center">
          <!-- Start -->
          <v-menu
            v-model="range.datePickerStart"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <p>
                <v-text-field
                  prepend-icon="mdi-calendar"
                  v-model="range.values.dateStart"
                  :label="$t('editEventCard.dateStart')"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  color="red darken-1"
                  class="ma-0"
                ></v-text-field>
              </p>
            </template>

            <v-date-picker
              v-if="range.datePickerStart"
              :locale="$i18n.locale"
              :max="range.values.dateEnd"
              v-model="range.values.dateStart"            
              @input="range.datePickerStart = false"
              color="grey"
            ></v-date-picker>
          </v-menu>
          
          <!-- End -->
          <v-menu
            v-model="range.datePickerEnd"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <p>
                <v-text-field
                  prepend-icon="mdi-calendar"
                  v-model="range.values.dateEnd"
                  :label="$t('editEventCard.dateEnd')"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  color="red darken-1"
                  class="ma-0"
                ></v-text-field>
              </p>
            </template>

            <v-date-picker
              v-if="range.datePickerEnd"
              v-model="range.values.dateEnd"
              :locale="$i18n.locale"
              :min="range.values.dateStart"            
              @input="range.datePickerEnd = false"
              color="grey"
            ></v-date-picker>
          </v-menu>
        </div>

        <!-- TimePickers -->
        <div class="d-flex justify-space-between align-center">
          <!-- Start -->
          <v-menu
            ref="pickerStart"
            v-model="range.timePickerStart"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <p>
                <v-text-field
                  v-model="range.values.timeStart"
                  :label="$t('editEventCard.timeStart')"
                  prepend-icon="mdi-clock"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  class="ma-0"
                ></v-text-field>
              </p>
            </template>

            <v-time-picker
              v-if="range.timePickerStart"            
              v-model="range.values.timeStart"
              format="24hr"
              :max="range.values.timeEnd"
              :allowed-minutes="allowedTimeSteps"
              scrollable
              @click:minute="range.timePickerStart = false"
            ></v-time-picker>
          </v-menu>

          <!-- End -->
          <v-menu
            ref="pickerEnd"
            v-model="range.timePickerEnd"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <p>
                <v-text-field
                  v-model="range.values.timeEnd"
                  :label="$t('editEventCard.timeEnd')"
                  prepend-icon="mdi-clock"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  class="ma-0"
                ></v-text-field>
              </p>
            </template>

            <v-time-picker
              v-if="range.timePickerEnd"            
              v-model="range.values.timeEnd"
              format="24hr"
              :min="range.values.timeStart"
              :allowed-minutes="allowedTimeSteps"
              scrollable
              @click:minute="range.timePickerEnd = false"
            ></v-time-picker>
          </v-menu>
        </div>

        <v-divider v-if="index + 1 < dateRanges.length"></v-divider>
      </div>

      <div class="px-3">
        <v-btn @click="addDateRange(0, '', '', '09:00', '17:00')" icon x-large><v-icon>mdi-calendar-plus</v-icon></v-btn>
      </div>
    </div>

    <div class="btn-action base d-flex justify-end mt-5">
        <v-btn text @click="$emit('closeSplitView')">
          {{ $t("editEventCard.cancel") }}
        </v-btn>

        <v-btn text color="success" @click="save">
          {{ $t("editEventCard.save") }}
        </v-btn>
    </div>
  </div>
</template>

<script>
import { getSubInterventionsByMasterId } from "/src/services/planning/InterventionServices.js";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import { mapActions } from "vuex";

export default {
  name: "split-event-info",
  props: {
    event: null
  },
  computed: {
  },
  data() {
    return {
      dateRanges: []
    }
  },
  mounted() {
    this.fetchRanges();
  },
  methods: {
    allowedTimeSteps : m => m % 5 === 0,
    ...mapActions("eventModule", ["splitIntervention"]),
    fetchRanges() {
      if (this.event.details.masterInterventionId === 0) {
        this.addDateRange(
          this.event.id,
          this.event.details.dateStart,
          this.event.details.dateEnd,
          this.event.details.timeStart,
          this.event.details.timeEnd
        );
      } else {
        getSubInterventionsByMasterId(this.event.details.masterInterventionId).then( res => {
          res.data.forEach(i => {
            this.addDateRange(
              i.id,
              i.dateStart,
              i.dateEnd,
              i.timeStart,
              i.timeEnd
            );
          });;
        });
      }
    },
    addDateRange(id, dateS, dateE, timeS, timeE) {
      this.dateRanges.push({
        datePickerStart: false,
        datePickerEnd: false,
        timePickerStart: false,
        timePickerEnd: false,
        values: {
          interventionId: id,
          dateStart: dateS,
          dateEnd: dateE,
          timeStart: timeS,
          timeEnd: timeE
        }
      });
    },
    save() {
      this.splitIntervention({interventionId: this.event.id, dateRanges: this.dateRanges.map(e => e.values)});
      this.$emit('closeSplitView');
    }
  },
  mixins: [validationMixin],
  validations: {
    dateRanges: {
      $each: {
        dateStart: {
          required
        },
        dateEnd: {
          required
        }
      }
    }
  }
}
</script>

<style lang="scss">
.split-wrapper {
  overflow-y: auto;
  overflow-x: hidden;
}

.btn-action {
  position: sticky;
  bottom: 0;
}
</style>